import React, {useEffect, useState} from "react";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import {useFormik} from "formik";
import {Col, Form, Modal, Row, Select, Switch} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../../hooks/useStore";
import {HideableInput} from "../../../../_defaultComponents/HideableInput/HideableInput";
import TagInput from "../../../../_defaultComponents/TagInput/TagInput";
export const IntegrationCreateModal = ({
    subcompanyId,
    visibility,
    onCancel,
    integrationsFields = [],
    existIntegrations = [],
    paymentTypes = [],
    paymentTypeRules = {}
}) => {
    const {t} = useTranslation(["integrations", "paymentTypes"]);
    const stores = useStore();
    const [initialValues, setInitialValues] = useState({});
    const [validation, setValidation] = useState(
        yup.object().shape({
            integrationType: yup
                .boolean()
                .required(t("integrations.validations.platformRequired", {ns: "integrations"}))
        })
    );
    const [confirmLoading, setConfirmLoading] = useState();
    const [tags, setTags] = useState([]);

    const formik = useFormik({
        initialValues: {
            isActive: true,
            canCreateOrder: true,
            canRunCallbacks: true,
            integrationType: undefined,
            supportedPaymentTypes: [],
            allowedOrderProviders: [],
            ...initialValues
        },
        validationSchema: validation,
        enableReinitialize: true,
        onSubmit: async values => {
            setConfirmLoading(true);
            try {
                const subCompanyId = subcompanyId;
                const {integrationType, ...integration} = values;

                integration.supportedPaymentTypes = integration.supportedPaymentTypes.filter(
                    paymentType =>
                        !paymentTypeRules[integrationType]?.disallowedTypes?.includes(paymentType)
                );

                await stores.companyStore.createSubcompanyIntegration({
                    subCompanyId,
                    integrationType,
                    integration
                });

                onCancel(true);
            } catch (error) {
                Sentry.captureException(error);
            } finally {
                setConfirmLoading(false);
            }
        }
    });

    const {values, errors, touched, handleSubmit, setFieldValue, resetForm} = formik;

    const generateInitialValuesByType = type => {
        const initialValues = {integrationType: type};
        integrationsFields.forEach(integration => {
            if (integration.type === type) {
                integration.exclusiveFields.forEach(exclusiveField => {
                    initialValues[exclusiveField] = "";
                });
            }
        });
        initialValues.supportedPaymentTypes = paymentTypeRules[type]?.defaultTypes || [];
        initialValues.allowedOrderProviders = [];
        setTags(initialValues.allowedOrderProviders);
        return initialValues;
    };
    const generateValidationByType = type => {
        const validation = {};
        integrationsFields.forEach(integration => {
            if (integration.type === type) {
                integration.exclusiveFields.forEach(exclusiveField => {
                    validation[exclusiveField] = yup
                        .string()
                        .required(t("validation.required", {ns: "common"}));
                });
            }
        });
        return yup.object().shape(validation);
    };
    const handleIntegrationTypeChange = type => {
        resetForm();
        setInitialValues(generateInitialValuesByType(type));
        setValidation(generateValidationByType(type));
    };

    const reset = () => {
        setInitialValues({});
        resetForm();
        setValidation(
            yup.object().shape({
                integrationType: yup
                    .boolean()
                    .required(t("integrations.validations.platformRequired", {ns: "integrations"}))
            })
        );
    };

    const handleChangeFlags = (field, checked) => {
        switch (field) {
            case "isActive":
                handleIsActiveChange(checked);
                break;
            case "canCreateOrder":
                handleCanCreateOrderChange(checked);
                break;
            case "canRunCallbacks":
                handleCanRunCallbacksChange(checked);
                break;
        }
    };

    const handleIsActiveChange = checked => {
        setFieldValue("isActive", checked);
        if (!checked) {
            setFieldValue("canCreateOrder", false);
            setFieldValue("canRunCallbacks", false);
        }
    };
    const handleCanCreateOrderChange = checked => {
        setFieldValue("canCreateOrder", checked);
        if (checked) {
            setFieldValue("isActive", true);
        } else {
            setFieldValue("canRunCallbacks", false);
        }
    };
    const handleCanRunCallbacksChange = checked => {
        setFieldValue("canRunCallbacks", checked);
        if (checked) {
            setFieldValue("isActive", true);
            setFieldValue("canCreateOrder", true);
        }
    };

    useEffect(() => reset(), [visibility]);

    useEffect(() => {
        setFieldValue("allowedOrderProviders", tags);
    }, [tags]);

    return (
        <Modal
            title={t("integrations.modals.createModal.title", {ns: "integrations"})}
            visible={visibility}
            onCancel={() => {
                onCancel(false);
            }}
            onOk={handleSubmit}
            okButtonProps={{disabled: !values.integrationType}}
            confirmLoading={confirmLoading}
            okText={t("modal.saveButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            <Form labelCol={{span: 24}} wrapperCol={{span: 24}}>
                <Row>
                    <Col span={24}>
                        <Form.Item
                            validateStatus={
                                errors.integrationType && touched.integrationType
                                    ? "error"
                                    : "success"
                            }
                            help={
                                errors.integrationType && touched.integrationType
                                    ? errors.integrationType
                                    : null
                            }
                        >
                            <h4>
                                {t("integrations.modals.createModal.platform", {
                                    ns: "integrations"
                                })}
                            </h4>
                            <Select
                                name="integrationType"
                                data-testid="integrationType"
                                value={values.integrationType}
                                placeholder={t(
                                    "integrations.modals.createModal.platformPlaceholder",
                                    {
                                        ns: "integrations"
                                    }
                                )}
                                onChange={handleIntegrationTypeChange}
                            >
                                {integrationsFields
                                    .filter(
                                        integration =>
                                            !existIntegrations
                                                ?.map(eIT => eIT?.type)
                                                ?.includes(integration?.type) ||
                                            (existIntegrations
                                                ?.map(eIT => eIT?.type)
                                                ?.includes(integration?.type) &&
                                                existIntegrations?.find(
                                                    eIT => eIT?.type === integration?.type
                                                )?.isMultiple)
                                    )
                                    .map(integration => (
                                        <Select.Option
                                            value={integration.type}
                                            key={integration.type}
                                        >
                                            {t(`integrations.platforms.${integration.type}.name`, {
                                                ns: "integrations"
                                            })}
                                        </Select.Option>
                                    ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    {integrationsFields.map(
                        integration =>
                            integration.type === values.integrationType &&
                            integration.exclusiveFields.map(exclusiveField => (
                                <Col span={24} key={`${integration.type}.${exclusiveField}`}>
                                    <Form.Item
                                        validateStatus={
                                            errors[exclusiveField] && touched[exclusiveField]
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            errors[exclusiveField] && touched[exclusiveField]
                                                ? errors[exclusiveField]
                                                : null
                                        }
                                    >
                                        <h4>
                                            {t(
                                                `integrations.platforms.${integration.type}.fields.${exclusiveField}`,
                                                {
                                                    ns: "integrations"
                                                }
                                            )}
                                        </h4>
                                        <HideableInput
                                            defaultHidden={false}
                                            value={values[exclusiveField]}
                                            onChange={value => {
                                                setFieldValue(exclusiveField, value);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            ))
                    )}
                    {values.integrationType && (
                        <Col span={24}>
                            <Form.Item
                                validateStatus={
                                    errors.supportedPaymentTypes && touched.supportedPaymentTypes
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.supportedPaymentTypes && touched.supportedPaymentTypes
                                        ? errors.supportedPaymentTypes
                                        : null
                                }
                            >
                                <h4>
                                    {t(`integrations.commonFields.supportedPaymentTypes`, {
                                        ns: "integrations"
                                    })}
                                </h4>
                                <Select
                                    name="supportedPaymentTypes"
                                    data-testid="supportedPaymentTypes"
                                    value={values.supportedPaymentTypes}
                                    placeholder={t(
                                        "integrations.commonFields.supportedPaymentTypesPlaceholder",
                                        {
                                            ns: "integrations"
                                        }
                                    )}
                                    mode="multiple"
                                    optionFilterProp="label"
                                    onChange={value =>
                                        setFieldValue("supportedPaymentTypes", value)
                                    }
                                >
                                    {paymentTypes
                                        .filter(
                                            paymentType =>
                                                !paymentTypeRules[
                                                    values.integrationType
                                                ]?.disallowedTypes?.includes(paymentType)
                                        )
                                        .map(paymentType => (
                                            <Select.Option
                                                value={paymentType}
                                                key={paymentType}
                                                label={t(`${paymentType}`, {ns: "paymentTypes"})}
                                            >
                                                {t(`${paymentType}`, {ns: "paymentTypes"})}
                                            </Select.Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}
                    {values.integrationType && (
                        <Col span={24}>
                            <Form.Item
                                validateStatus={
                                    errors.allowedOrderProviders && touched.allowedOrderProviders
                                        ? "error"
                                        : "success"
                                }
                                help={
                                    errors.allowedOrderProviders && touched.allowedOrderProviders
                                        ? errors.allowedOrderProviders
                                        : null
                                }
                            >
                                <h4>
                                    {t(`integrations.commonFields.allowedOrderProviders`, {
                                        ns: "integrations"
                                    })}
                                </h4>
                                <TagInput
                                    tags={tags}
                                    setTags={setTags}
                                    addTitle={t(
                                        `integrations.commonFields.addAllowedOrderProvider`,
                                        {
                                            ns: "integrations"
                                        }
                                    )}
                                    addPlaceHolderText={t(
                                        `integrations.commonFields.allowedOrderProvidersPlaceholder`,
                                        {
                                            ns: "integrations"
                                        }
                                    )}
                                ></TagInput>
                            </Form.Item>
                        </Col>
                    )}
                    {values.integrationType &&
                        ["isActive", "canCreateOrder", "canRunCallbacks"].map(field => (
                            <Col span={24} key={field}>
                                <Form.Item
                                    validateStatus={
                                        errors[field] && touched[field] ? "error" : "success"
                                    }
                                    help={errors[field] && touched[field] ? errors[field] : null}
                                >
                                    <h4>
                                        {t(`integrations.commonFields.${field}`, {
                                            ns: "integrations"
                                        })}
                                    </h4>
                                    <Switch
                                        checked={values[field]}
                                        checkedChildren={t("other.active", {ns: "common"})}
                                        unCheckedChildren={t("other.passive", {ns: "common"})}
                                        data-testid={field}
                                        onChange={checked => handleChangeFlags(field, checked)}
                                    />
                                </Form.Item>
                            </Col>
                        ))}
                </Row>
            </Form>
        </Modal>
    );
};
